import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Modal } from 'react-bootstrap';
import Dropdown from 'react-bootstrap/Dropdown';
import { CFD, ETF, FX } from 'shared-modules/constants';
import { KEY_FOR_PORTFOLIO_PL_TREND_SWAP_MODE, SWAP_DISPLAY_REALIZED } from 'shared-modules/constants/portfolio';
import { getPortfolioPlTrendSwapDisplayOptions } from 'shared-modules/utils/profitLossUtils';
import { getShortName, removeSuffix } from 'shared-modules/hooks/symbol';
import useSwapDisplayOptions from 'shared-modules/hooks/useSwapDisplayOptions';
import { getDefaultValuesFromLocalStorage, saveDefaultValuesFromLocalStorage } from 'shared-modules/services';
import useProfitLossFilter from 'shared-modules/services/hooks/useProfitLossFilter';
import CustomCurrencyIcon from '../../../../components/CustomCurrencyIcon';
import { Button, Table, InfoTooltip } from '../../../../components';
import PLCalendar from '../../../../components/PLCalendar/PLCalendar';
import { ColoredPriceText, NumberText } from '../../../../components/Texts';
import MonthItemButton from './MonthItemButton';

import styles from './plTrendTab.module.scss';

const RESIZED = {
  [FX]: {
    KEY: `portfolioPlTotalFX`,
    COLUMNS: [120, 112, 112, 68, 68],
  },
  [ETF]: {
    KEY: `portfolioPlTotalETF`,
    COLUMNS: [120, 112, 114, 68, 68],
  },
  [CFD]: {
    KEY: `portfolioPlTotalCFD`,
    COLUMNS: [120, 112, 114, 68, 68],
  },
};

export const PlTrendTab = memo(({ serviceId }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [isSwapDropdownOpen, setIsSwapDropdownOpen] = useState(false);
  const [selectedSwapDisplay, setSelectedSwapDisplay] = useState(
    getDefaultValuesFromLocalStorage({
      key: KEY_FOR_PORTFOLIO_PL_TREND_SWAP_MODE,
      defaultValue: SWAP_DISPLAY_REALIZED,
    }),
  );

  const {
    instrumentList,
    plByInstrument,
    chartDataRealized,
    chartDataUnrealized,
    totalPl,
    totalRealizedSwapPl,
    totalUnrealizedSwapPl,
    numberOfEntries,
    numberOfExits,
    monthOptions,
    handleMonthSelect,
    selectedMonth,
    resetSelectedMonth,
    currentFrontDate,
  } = useProfitLossFilter(serviceId);

  const selectedMonthIndex = monthOptions.findIndex((item) => item.value === selectedMonth.value);
  const prevMonth = monthOptions[Math.min(selectedMonthIndex + 1, monthOptions.length - 1)];
  const nextMonth = monthOptions[Math.max(selectedMonthIndex - 1, 0)];
  const isFirst = selectedMonthIndex === 0;
  const isLast = selectedMonthIndex === monthOptions.length - 1;

  const handleSelectMonthModal = useCallback(
    (item) => {
      handleMonthSelect(item);
      setModalVisible(false);
    },
    [handleMonthSelect, setModalVisible],
  );

  const swapDisplayOptions = useMemo(() => getPortfolioPlTrendSwapDisplayOptions({ serviceId }), [serviceId]);

  const {
    selectedSwapOption,
    totalPlTooltipMessage,
    realizedPlTooltipMessage,
    swapTooltipMessage,
    getSwapPl,
    getChartData,
  } = useSwapDisplayOptions({ serviceId, selectedSwapDisplay });

  const chartData = getChartData(chartDataRealized, chartDataUnrealized);
  const totalSwapPl = getSwapPl(totalRealizedSwapPl, totalUnrealizedSwapPl);

  useEffect(() => {
    saveDefaultValuesFromLocalStorage({
      key: KEY_FOR_PORTFOLIO_PL_TREND_SWAP_MODE,
      value: selectedSwapDisplay,
    });
  }, [selectedSwapDisplay]);

  const handleSelectSwapDisplay = useCallback((value) => {
    setSelectedSwapDisplay(value);
    setIsSwapDropdownOpen(false);
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: '銘柄',
        accessor: 'instrumentId',
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell: { value } }) => (
          <div className="d-flex flex-row">
            <div className="me-2">
              <CustomCurrencyIcon isMini currencyPair={value} className={classNames(styles.iconBorder)} />
            </div>
            <div className="d-flex align-items-center text-start">
              {serviceId === FX ? removeSuffix(value) : getShortName(value, instrumentList)}
            </div>
          </div>
        ),
        isOrderName: true,
        className: classNames(styles.tableHeaderCol, styles.name),
        disableResizing: true,
      },
      {
        Header: '実現損益',
        accessor: 'pl',
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell: { value } }) => (
          <ColoredPriceText className={styles.cellValue} symbolClassName={styles.cellValue} value={value} allowZero />
        ),
        isNumber: true,
        className: classNames(styles.tableHeaderCol, styles.number),
        disableResizing: true,
      },
      {
        Header: selectedSwapOption.label,
        accessor: selectedSwapDisplay === SWAP_DISPLAY_REALIZED ? 'realizedSwapPl' : 'unrealizedSwapPl',
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell: { value } }) => (
          <ColoredPriceText className={styles.cellValue} symbolClassName={styles.cellValue} value={value} allowZero />
        ),
        isNumber: true,
        className: classNames(styles.tableHeaderCol, styles.number),
        disableResizing: true,
      },
      {
        Header: '新規',
        accessor: 'openExecutionCount',
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell: { value } }) => (
          <NumberText className={styles.cellValue} symbolClassName={styles.cellValue} value={value} symbol="回" />
        ),
        isNumber: true,
        className: classNames(styles.tableHeaderCol, styles.number),
        disableResizing: true,
      },
      {
        Header: '決済',
        accessor: 'closeExecutionCount',
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell: { value } }) => (
          <NumberText className={styles.cellValue} symbolClassName={styles.cellValue} value={value} symbol="回" />
        ),
        isNumber: true,
        className: classNames(styles.tableHeaderCol, styles.number),
        disableResizing: true,
      },
    ],
    [instrumentList, selectedSwapDisplay, selectedSwapOption.label, serviceId],
  );

  const resized = useMemo(() => {
    const { KEY, COLUMNS } = RESIZED[serviceId];
    return {
      key: KEY,
      default: [...COLUMNS],
    };
  }, [serviceId]);

  return (
    <div className={classNames('d-flex flex-column pt-3', styles.tabContent, styles.gap24)}>
      <div className={styles.headerControlRow}>
        <div className={classNames('d-flex flex-row', styles.gap10, styles.headerControlLeft)}>
          <i className={classNames('material-icons-outlined', styles.calendarIcon)}>calendar_today</i>
          <Button className={styles.calendarBtn} onClick={() => setModalVisible(true)} outlined>
            <span className={styles.selectedMonth}>{selectedMonth.label}</span>
            <i className="material-icons-outlined">keyboard_arrow_down</i>
          </Button>
          <Button
            className={classNames(styles.changeMonthBtn, styles.changeMonthBtnFirst)}
            outlined
            onClick={() => handleMonthSelect(prevMonth)}
            disabled={isLast}
          >
            <i className="material-icons-outlined">keyboard_arrow_left</i>
          </Button>
          <Button
            className={styles.changeMonthBtn}
            outlined
            onClick={() => handleMonthSelect(nextMonth)}
            disabled={isFirst}
          >
            <i className="material-icons-outlined">keyboard_arrow_right</i>
          </Button>
          <Button className={styles.thisMonthBtn} outlined onClick={resetSelectedMonth} disabled={isFirst}>
            今月
          </Button>
        </div>
        <div className={styles.headerControlRight}>
          <Dropdown
            align="end"
            show={isSwapDropdownOpen}
            onToggle={(nextShow) => setIsSwapDropdownOpen(nextShow)}
            className={styles.swapSelectWrapper}
          >
            <Dropdown.Toggle className={styles.swapSelectBtn} bsPrefix={styles.swapSelectBtn}>
              <span>{selectedSwapOption.label}</span>
              <i className="material-icons-outlined">keyboard_arrow_down</i>
            </Dropdown.Toggle>
            <Dropdown.Menu className={styles.swapOptionsDropdown}>
              <div className={styles.swapOptionsTitle}>スワップ表示切替</div>
              {swapDisplayOptions.map((option) => {
                const isSelected = option.value === selectedSwapDisplay;
                const optionId = `pl-trend-swap-display-${option.value.toLowerCase()}`;

                return (
                  <div key={option.value}>
                    <input
                      type="radio"
                      id={optionId}
                      className={styles.swapOptionRadio}
                      name="pl-trend-swap-display"
                      value={option.value}
                      checked={isSelected}
                      onChange={() => handleSelectSwapDisplay(option.value)}
                    />
                    <label htmlFor={optionId} className={styles.swapOptionRow}>
                      <i className="material-icons-outlined">
                        {isSelected ? 'radio_button_checked' : 'radio_button_unchecked'}
                      </i>
                      <div className={styles.swapOptionTextBlock}>
                        <div className={styles.swapOptionLabel}>{option.label}</div>
                        <div className={styles.swapOptionDescription}>{option.description}</div>
                      </div>
                    </label>
                  </div>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      <div className={classNames('d-flex flex-row', styles.gap24)}>
        <div className={styles.calendarContainer}>
          <PLCalendar month={selectedMonth} data={chartData} currentFrontDate={currentFrontDate} />
        </div>
        <div className={classNames('d-flex flex-column', styles.leftContainer, styles.gap24)}>
          <div className={classNames('card d-flex flex-column', styles.cardLayout, styles.gap8)}>
            <div className="d-flex justify-content-between">
              <div className="d-flex flex-row align-items-center">
                <div className={styles.cardLabel}>損益合計</div>
                <InfoTooltip message={totalPlTooltipMessage} />
              </div>

              <ColoredPriceText
                className={styles.plTotal}
                symbolClassName={styles.symbol}
                value={totalPl + totalSwapPl}
                allowZero
              />
            </div>
            <div className="d-flex justify-content-between">
              <div className={classNames('d-flex justify-content-between', styles.smallItem)}>
                <div className="d-flex flex-row">
                  <div className={styles.cardLabel}>実現損益</div>
                  <InfoTooltip message={realizedPlTooltipMessage} />
                </div>
                <ColoredPriceText
                  className={styles.cardValue}
                  symbolClassName={styles.symbol}
                  value={totalPl}
                  allowZero
                />
              </div>
              <div className={classNames('d-flex justify-content-between', styles.smallItem)}>
                <div className="d-flex flex-row">
                  <div className={styles.cardLabel}>{selectedSwapOption.label}</div>
                  <InfoTooltip message={swapTooltipMessage} />
                </div>
                <ColoredPriceText
                  className={styles.cardValue}
                  symbolClassName={styles.symbol}
                  value={totalSwapPl}
                  allowZero
                />
              </div>
            </div>
            <div className="d-flex justify-content-between">
              <div className={classNames('d-flex justify-content-between', styles.smallItem)}>
                <div className={styles.cardLabel}> 新規 </div>
                <NumberText
                  className={styles.cardValue}
                  symbolClassName={styles.symbol}
                  value={numberOfEntries}
                  symbol="回"
                />
              </div>
              <div className={classNames('d-flex justify-content-between', styles.smallItem)}>
                <div className={styles.cardLabel}>決済</div>
                <NumberText
                  className={styles.cardValue}
                  symbolClassName={styles.symbol}
                  value={numberOfExits}
                  symbol="回"
                />
              </div>
            </div>
          </div>
          <div>
            <h2 className={styles.tableTitle}>銘柄ごとの損益合計</h2>
            <div className={styles.tableContainer}>
              <Table
                tableData={plByInstrument}
                columns={columns}
                resized={resized}
                emptyText="選択中の期間に損益は発生していません。"
                rowHight={64}
              />
            </div>
          </div>
        </div>
      </div>
      <Modal
        contentClassName="popup popup--fit-content"
        show={modalVisible}
        onHide={() => setModalVisible(false)}
        keyboard={false}
        aria-labelledby="contained-modal-title-vcenter"
        size="lg"
        className={styles.modal}
        backdropClassName={styles.backdrop}
        centered
      >
        <Modal.Header className="popup__header" closeButton closeVariant="white">
          <Modal.Title className={styles.modalTitle}>期間指定</Modal.Title>
        </Modal.Header>
        <Modal.Body className="popup__body">
          <div className="container pt-2">
            <div className="row row-cols-3">
              {monthOptions.map((item) => (
                <div className="col" style={{ padding: 5 }} key={item.value}>
                  <MonthItemButton
                    isActive={item.value === selectedMonth.value}
                    onClick={() => handleSelectMonthModal(item)}
                  >
                    {item.label}
                  </MonthItemButton>
                </div>
              ))}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
});

PlTrendTab.propTypes = {
  serviceId: PropTypes.string.isRequired,
};
